import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { usePostHog } from 'posthog-js/react'
import { erc20Abi } from 'viem'
import {
  useAccount,
  usePublicClient,
  useSwitchChain,
  useWriteContract,
} from 'wagmi'

import { active_chain } from '@repo/common/blockchain/config'

import { FissionVaultAbi } from '../blockchain/abis/FissionVault'
import { useCreateAndSignPermit2 } from '../blockchain/useCreateAndSignPermit2'
import {
  set_looking_for_change,
  keys as wallet_keys,
} from '../blockchain/useGetWalletBalance'
import { amount_to_tokens, tokens_to_amount } from '../helpers/block_helpers'
import { disableKeyWhenMissing, reportEnableFailed } from '../helpers/utilities'
import { useGetSymbolToDecimal } from '../portfolio'
import { keys as portfolio_keys } from '../portfolio'
import { useGetTokenAddress } from '../products'

import { keys, useVaultInfo } from './vaults_queries'

export type VaultBalances = {
  usdc_balance: bigint
  fund_balance: bigint
  usdc_auto_position_amount: bigint
  fund_auto_position_amount: bigint
  usdc_manual_position_amount: bigint
  fund_manual_position_amount: bigint
}

export function useGetVaultBalances({ id }: { id: RubyID | undefined }) {
  const vault_address = useVaultInfo({ id }).data?.vault_contract_address
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled = id != null && client != null && vault_address != null

  reportEnableFailed({
    name: 'useGetVaultBalances',
    parts: {
      id,
      client,
      vault_address,
    },
  })

  return useQuery<VaultBalances>({
    queryKey: keys.vaults.details({ id: disableKeyWhenMissing(id) })._ctx
      .balances.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const result = await client.readContract({
            abi: FissionVaultAbi,
            functionName: 'vaultBalances',
            address: vault_address,
          })

          return {
            usdc_balance: result.fireblocksWalletBalance.usdcTokensAmount,
            fund_balance: result.fireblocksWalletBalance.fundTokensAmount,
            usdc_auto_position_amount:
              result.autoPositionBalance.usdcTokensAmount,
            fund_auto_position_amount:
              result.autoPositionBalance.fundTokensAmount,
            usdc_manual_position_amount:
              result.manualPositionBalance.usdcTokensAmount,
            fund_manual_position_amount:
              result.manualPositionBalance.fundTokensAmount,
          }
        },
  })
}

export function useGetVaultTotalTokens({ id }: { id: RubyID | undefined }) {
  const { data } = useVaultInfo({ id })
  const token_address = data?.token_address
  const symbol = data?.symbol
  const symbol_to_decimal = useGetSymbolToDecimal()

  const client = usePublicClient({ chainId: active_chain.id })

  const enabled =
    id != null &&
    client != null &&
    token_address != null &&
    symbol != null &&
    symbol_to_decimal != null

  reportEnableFailed({
    name: 'useGetVaultTotalTokens',
    parts: {
      id,
      client,
      token_address,
      symbol,
      symbol_to_decimal,
    },
  })

  return useQuery<number>({
    queryKey: keys.vaults.details({ id: disableKeyWhenMissing(id) })._ctx
      .total_tokens.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const shares = await client.readContract({
            abi: erc20Abi,
            functionName: 'totalSupply',
            address: token_address,
          })
          return amount_to_tokens(shares, symbol_to_decimal[symbol])
        },
  })
}

// eslint-disable-next-line complexity, max-lines-per-function
export function useBuyVault({ id }: { id: RubyID }) {
  const queryClient = useQueryClient()
  const { writeContractAsync } = useWriteContract()
  const { switchChainAsync } = useSwitchChain()
  const { address: wallet_address } = useAccount()
  const { data } = useVaultInfo({ id })
  const minter_address = data?.vault_contract_address
  const fund_symbol = 'TECH'
  const symbol_to_decimal = useGetSymbolToDecimal()

  const usdc_address = useGetTokenAddress({ symbol: 'USDC' })
  const fund_address = useGetTokenAddress({ symbol: fund_symbol })

  const client = usePublicClient({ chainId: active_chain.id })

  const { createDoubleAsync, isReady: permit_is_ready } =
    useCreateAndSignPermit2()
  const posthog = usePostHog()

  const isReady =
    permit_is_ready &&
    wallet_address != null &&
    symbol_to_decimal != null &&
    usdc_address != null &&
    fund_address != null &&
    minter_address != null &&
    client != null

  reportEnableFailed({
    name: 'useBuyVault',
    parts: {
      permit_is_ready,
      wallet_address,
      symbol_to_decimal,
      usdc_address,
      fund_address,
      minter_address,
      client,
    },
  })

  const result = useMutation({
    mutationFn: async ({
      deposit_usdc_tokens,
      deposit_fund_tokens,
    }: {
      deposit_usdc_tokens: number
      deposit_fund_tokens: number
    }) => {
      if (!isReady) return

      // ensure network is correct
      await switchChainAsync({ chainId: active_chain.id })

      const deposit_usdc_amount = tokens_to_amount(
        deposit_usdc_tokens,
        symbol_to_decimal['USDC'],
      )
      const deposit_fund_amount = tokens_to_amount(
        deposit_fund_tokens,
        symbol_to_decimal[fund_symbol],
      )

      const { permitDouble, signature } = await createDoubleAsync({
        tokens: [
          {
            symbol: fund_symbol,
            address: fund_address,
            amount: deposit_fund_amount,
          },
          {
            symbol: 'USDC',
            address: usdc_address,
            amount: deposit_usdc_amount,
          },
        ],
        destination_address: minter_address,
      })

      const tx = await writeContractAsync({
        abi: FissionVaultAbi,
        address: minter_address,
        functionName: 'depositWithPermit2',
        args: [
          deposit_fund_amount,
          deposit_usdc_amount,
          permitDouble,
          signature,
        ],
        chainId: active_chain.id,
      })

      await client.waitForTransactionReceipt({ hash: tx })
      posthog.capture('vault minted', {
        fund: fund_symbol,
        deposit_usdc_tokens,
        deposit_fund_tokens,
      })
    },
    onSuccess: async () => {
      set_looking_for_change(data!.token_address)
      await queryClient.invalidateQueries({
        queryKey: wallet_keys.balance.wallet(wallet_address!).queryKey,
      })

      await queryClient.invalidateQueries({
        queryKey: portfolio_keys.portfolio._def,
      })
    },
  })
  return {
    ...result,
    isReady,
  }
}

export function useRedeemVault({ id }: { id: RubyID }) {
  const queryClient = useQueryClient()
  const { writeContractAsync } = useWriteContract()
  const { switchChainAsync } = useSwitchChain()
  const { data: vault } = useVaultInfo({ id })
  const symbol_to_decimal = useGetSymbolToDecimal()
  const client = usePublicClient({ chainId: active_chain.id })
  const { address: wallet_address } = useAccount()

  const isReady =
    client != null &&
    vault != null &&
    symbol_to_decimal != null &&
    wallet_address != null

  const result = useMutation({
    mutationFn: async ({ tokens }: { tokens: number }) => {
      if (!isReady) return

      // ensure network is correct
      await switchChainAsync({ chainId: active_chain.id })

      const tx = await writeContractAsync({
        abi: FissionVaultAbi,
        address: vault.vault_contract_address,
        functionName: 'withdraw',
        chainId: active_chain.id,
        args: [
          tokens_to_amount(tokens, symbol_to_decimal[vault.symbol]),
          wallet_address,
        ],
      })

      await client.waitForTransactionReceipt({ hash: tx })
    },
    onSuccess: async () => {
      set_looking_for_change(vault!.token_address)
      await queryClient.invalidateQueries({
        queryKey: wallet_keys.balance.wallet(wallet_address!).queryKey,
      })

      await queryClient.invalidateQueries({
        queryKey: portfolio_keys.portfolio._def,
      })
    },
  })
  return {
    ...result,
    isReady,
  }
}
